import { useLayoutEffect, useRef, useState } from "react";
import type { ComponentType, SVGProps } from "react";
import { keyframes, styled } from "@pigment-css/react";
import { CheckMark2Small, ChevronGrabberVertical } from "@acto/web-common/components";
import { menuItemCheckStyle, menuItemStyle, menuSurfaceStyle } from "@acto/taskpane/field-sales-2/styles/glassMenu";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";

// Generic select dropdown: a trigger plus a glass popover of checkmarked options. Per `variant`
// the trigger renders as large inline text, a glass pill, or an inline section title; the dropdown
// list is identical across all. Calendar-specific concerns (e.g. a calendar icon per option) live
// in `CalendarSelectMenu`, which wraps this primitive — this component has no calendar knowledge.

const Anchor = styled("div")<{ align: "left" | "right" }>({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  // Hug content (don't stretch the trigger full-width) so a centred press-scale reads as a uniform
  // shrink instead of sliding the left-aligned label toward the card's centre.
  alignItems: "flex-start",
  variants: [{ props: { align: "right" }, style: { alignItems: "flex-end" } }],
});

const Caption = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  color: theme.fieldSalesTwo.colors.neutral[700],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
}));

// used for the month selector
const TextTrigger = styled("button")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tight,
  color: theme.fieldSalesTwo.colors.neutral[900],
  // Lift above the outside-click Backdrop so tapping the trigger to close hits the button (firing
  // its press animation) rather than the overlay.
  position: "relative",
  zIndex: 6,
  // pressInteraction only sets transform on :active; it needs transform in the transition to animate.
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  "& svg": { width: 18, height: 18 },
  ...pressInteraction(theme, { scale: 0.97 }),
}));

// used for an inline section title
const SectionTitleTrigger = styled("button")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[6],
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& svg": { width: 20, height: 20, flexShrink: 0 },
  // Lift above the outside-click Backdrop so tapping the title to close hits the button (firing its
  // press animation) rather than the overlay.
  position: "relative",
  zIndex: 6,
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  ...pressInteraction(theme, { scale: 1 }),
}));

// used for year selection in the date range picker
const PillTrigger = styled("button")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  height: theme.fieldSalesTwo.spacing[48],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& svg": { width: 16, height: 16, color: theme.fieldSalesTwo.colors.neutral[700] },
  // Lift above the outside-click Backdrop so tapping the pill to close hits the button (firing its
  // press animation) rather than the overlay.
  position: "relative",
  zIndex: 6,
  // pressInteraction only sets transform on :active; it needs transform in the transition to animate.
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  ...pressInteraction(theme, { scale: 0.97 }),
}));

const IconTrigger = styled("button")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[48],
  height: theme.fieldSalesTwo.spacing[48],
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
  position: "relative",
  zIndex: 6,
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  ...pressInteraction(theme, { scale: 0.92 }),
}));

const HeaderIconTrigger = styled(HeaderIconButton)(({ theme }) => ({
  position: "relative",
  zIndex: 6,
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
}));

// Covers the viewport only while no ancestor has a transform, filter or backdrop-filter; such an
// ancestor becomes its containing block and limits outside-tap-to-close to that ancestor's box.
const Backdrop = styled("div")({
  position: "fixed",
  inset: 0,
  zIndex: 5,
});

const dropdownIn = keyframes({
  from: { opacity: 0, transform: "translateY(-8px) scale(0.96)" },
  to: { opacity: 1, transform: "translateY(0) scale(1)" },
});

const dropdownFade = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const Dropdown = styled("div")<{ align: "left" | "right" }>(({ theme }) => ({
  ...menuSurfaceStyle(theme),
  position: "absolute",
  top: "100%",
  zIndex: 6,
  marginTop: theme.fieldSalesTwo.spacing[8],
  minWidth: "14rem",
  maxHeight: "15rem",
  overflowY: "auto",
  overscrollBehavior: "contain",
  // Extra top padding above the first item, unlike the uniform popover surface padding.
  padding: `${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]}`,
  animation: `${dropdownIn} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: `${dropdownFade} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  },
  variants: [
    { props: { align: "left" }, style: { left: 0, transformOrigin: "top left" } },
    { props: { align: "right" }, style: { right: 0, transformOrigin: "top right" } },
  ],
}));

const Item = styled("button")(({ theme }) => menuItemStyle(theme));

const ItemIcon = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
}));

const ItemLabel = styled("span")({
  flex: 1,
  minWidth: 0,
});

const ItemCheck = styled("span")(({ theme }) => menuItemCheckStyle(theme));

export interface SelectOption {
  value: number;
  label: string;
}

interface SelectMenuBaseProps {
  options: SelectOption[];
  value: number;
  onSelect: (value: number) => void;
  /** aria-label for the trigger. */
  triggerLabel: string;
  /** Small caption above a text trigger (e.g. the year above a month name). */
  caption?: string;
  /** Icon rendered before every option's label (e.g. a calendar icon for date pickers). Omitted by
   *  default — most menus (breakdown/view toggles) are plain text options with no icon. */
  itemIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Dropdown alignment relative to the trigger (default "left"). */
  align?: "left" | "right";
  /** Overrides the trigger text (default = the selected option's label). Options still drive the menu. */
  displayLabel?: string;
}

/** "text", "pill" and "sectionTitle" show the selected label with a chevron; "icon" and "headerIcon" show
 *  only `triggerIcon`. Defaults to "text". */
export type SelectMenuProps = SelectMenuBaseProps &
  (
    | { variant?: "text" | "pill" | "sectionTitle"; triggerIcon?: never }
    | { variant: "icon" | "headerIcon"; triggerIcon: ComponentType<SVGProps<SVGSVGElement>> }
  );

export function SelectMenu({
  options,
  value,
  onSelect,
  triggerLabel,
  variant = "text",
  caption,
  itemIcon: ItemIconComponent,
  align = "left",
  displayLabel,
  triggerIcon: TriggerIcon,
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const Trigger =
    variant === "icon"
      ? IconTrigger
      : variant === "headerIcon"
        ? HeaderIconTrigger
        : variant === "pill"
          ? PillTrigger
          : variant === "sectionTitle"
            ? SectionTitleTrigger
            : TextTrigger;
  const valueLabel = displayLabel ?? options.find((option) => option.value === value)?.label ?? "";

  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  // When the dropdown opens, center the currently-selected option in view (scrolls only the
  // dropdown, not any outer scroll container).
  useLayoutEffect(() => {
    if (!open) return;
    const dropdown = dropdownRef.current;
    const item = selectedItemRef.current;
    if (!dropdown || !item) return;
    dropdown.scrollTop = item.offsetTop - dropdown.clientHeight / 2 + item.offsetHeight / 2;
  }, [open]);

  return (
    <Anchor align={align}>
      {variant === "text" && caption && <Caption>{caption}</Caption>}
      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={triggerLabel}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        {TriggerIcon ? (
          <TriggerIcon aria-hidden="true" />
        ) : (
          <>
            {valueLabel}
            <ChevronGrabberVertical aria-hidden="true" />
          </>
        )}
      </Trigger>
      {open && (
        <>
          <Backdrop onClick={() => setOpen(false)} />
          <Dropdown ref={dropdownRef} role="listbox" align={align}>
            {options.map((option) => (
              <Item
                key={option.value}
                ref={option.value === value ? selectedItemRef : undefined}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onSelect(option.value);
                  setOpen(false);
                }}
              >
                {ItemIconComponent && (
                  <ItemIcon>
                    <ItemIconComponent aria-hidden="true" />
                  </ItemIcon>
                )}
                <ItemLabel>{option.label}</ItemLabel>
                {option.value === value && (
                  <ItemCheck>
                    <CheckMark2Small aria-hidden="true" />
                  </ItemCheck>
                )}
              </Item>
            ))}
          </Dropdown>
        </>
      )}
    </Anchor>
  );
}
